import { defineComponent as _defineComponent } from 'vue'
import Vue, { computed } from 'vue';

import ProviderIcon from '@/components/picture/ProviderIcon.vue';
import PictureComp from '@/components/picture/PictureComp.vue';
import type { SnowplowContext } from '@/helpers/tracking/providers';
import type { CatalogueItem } from '@/helpers/promotion-helper';

import { OfferType } from '@/@types/graphql-types';
import type { SponsoredAdFragment } from '@/features/sponsored-recommendations/graphql/fragments/SponsoredAd.fragment';
import type { TitleDetail } from '@/interfaces/title-details-graphql';
import type { TitleOfferFragment } from '@/features/buybox/graphql/fragments/Offer.fragment';
import type { Maybe } from '@/helpers/types';
import type { ThumborOptions } from '@/filters/thumbor';
import { NewBuyBoxView } from '@/stores/useUserStoreTypes';


export default /*#__PURE__*/_defineComponent({
  __name: 'SponsoredRecommendationSingleQTV',
  props: {
    sponsoredAd: null,
    currentTitle: null,
    lazy: { type: Boolean },
    additionalContexts: null,
    displayPromotedTag: { type: Boolean, default: true },
    buyboxView: { default: NewBuyBoxView.LIST }
  },
  emits: ["onPosterClickOut", "onWatchNowClick", "imageError"],
  setup(__props: any, { emit: emits }: { emit: ({
	(e: 'onPosterClickOut'): void;
	(e: 'onWatchNowClick'): void;
	(e: 'imageError', src: Maybe<string>): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { sponsoredAd: SponsoredAdFragment, currentTitle: TitleDetail, lazy: boolean, additionalContexts: SnowplowContext[], displayPromotedTag: boolean, buyboxView: NewBuyBoxView };

const ListSQTV = () => import('@/features/buybox/components/list/SingleQTV.vue');
const GridSQTV = () => import('@/features/buybox/components/grid/GridSQTV.vue');





function handlePosterClickOut() {
	emits('onPosterClickOut');
}

function handleWatchNowClick() {
	emits('onWatchNowClick');
}

function handleImageError(src: Maybe<string>) {
	emits('imageError', src);
}

const sqtvComponent = computed(() => (props.buyboxView === NewBuyBoxView.GRID ? GridSQTV : ListSQTV));

const providerShortName = computed(() => props.sponsoredAd?.campaign?.watchNowOffer?.package?.shortName || undefined);

const adaptedTitle = computed((): TitleDetail | null => {
	const node = props.sponsoredAd?.campaign?.node;
	if (!node) return null;

	if (node.__typename !== 'Movie' && node.__typename !== 'Show' && node.__typename !== 'Season') {
		return null;
	}

	return {
		...node,
		id: node.nodeId,
		content: {
			...node.content,
			tags: [],
		},
		// Cast through unknown to bypass strict type checking
	} as unknown as TitleDetail;
});

const adaptedCatalogueItem = computed((): CatalogueItem | null => {
	const pkg = props.sponsoredAd?.campaign?.watchNowOffer?.package;
	if (!pkg) return null;

	return {
		packageId: pkg.packageId,
		clearName: pkg.clearName,
		technicalName: pkg.technicalName,
		shortName: pkg.shortName,
		iconWide: pkg.iconWide,
		offer: (_offer, _country, _inCatalogue) => {
			return enrichWatchNowOffer(props.sponsoredAd.campaign!.watchNowOffer);
		},
		priority: 0,
		include: true,
		placement: 'sqtv' as const,
	};
});

const campaignDetails = computed(() => props.sponsoredAd.campaign);
/** List uses wide logo vs. grid uses regular logo */
const customProviderIconUrl = computed(() =>
	props.buyboxView === NewBuyBoxView.GRID
		? campaignDetails.value?.promotionalProviderLogo
		: campaignDetails.value?.promotionalProviderWideLogo
);
const hasCustomIcon = computed(() => !!customProviderIconUrl.value);
const hasCustomTitle = computed(() => campaignDetails.value?.promotionalTitle);
const hasCustomCtaText = computed(() => campaignDetails.value?.watchNowLabel);
const hasDisclaimerText = computed(() => campaignDetails.value?.disclaimerText);
const hasCustomPromotionalText = computed(
	() => campaignDetails.value?.hideRatings && campaignDetails.value?.promotionalText
);
const hasCustomBannerText = computed(() => campaignDetails.value?.bannerText);
const hasCustomPoster = computed(() => campaignDetails.value?.posterOverride);
const hasCustomBackdrop = computed(() => campaignDetails.value?.promotionalImageUrl);

function imageUrl(titlePosterUrl: string | null | undefined, type: 'poster' | 'backdrop' = 'poster') {
	if (!titlePosterUrl) {
		return undefined;
	}
	return Vue.filter('Thumbor')({
		url: titlePosterUrl,
		imageType: type,
	} as ThumborOptions)?.replace('{format}', 'jpg');
}

function enrichWatchNowOffer(
	watchNowOffer: NonNullable<NonNullable<SponsoredAdFragment['campaign']>['watchNowOffer']>
): TitleOfferFragment {
	return {
		...watchNowOffer,
		type: OfferType.Standard,
		country: '', // Not critical for sponsored offers
		subtitleLanguages: [],
		videoTechnology: [],
		audioTechnology: [],
		audioLanguages: [],
		userLocalCurrency: null,
		retailPriceConverted: null,
		elementCount: null,
		package: {
			...watchNowOffer.package,
			planOffers: [],
		},
		plans: [],
	} as TitleOfferFragment;
}

return { __sfc: true,ListSQTV, GridSQTV, props, emits, handlePosterClickOut, handleWatchNowClick, handleImageError, sqtvComponent, providerShortName, adaptedTitle, adaptedCatalogueItem, campaignDetails, customProviderIconUrl, hasCustomIcon, hasCustomTitle, hasCustomCtaText, hasDisclaimerText, hasCustomPromotionalText, hasCustomBannerText, hasCustomPoster, hasCustomBackdrop, imageUrl, enrichWatchNowOffer, ProviderIcon, PictureComp, NewBuyBoxView }
}

})